import { BoxTruckVerification, fetchMyBoxTruckDrivers } from '../services/boxTruckLoadsApi'

/**
 * Every driver on the roster a search matches, not just the page on screen.
 *
 * ONE walk, used by both pickers. The Mailing form and the SMS composer were doing the same thing side
 * by side — page one for the count, then the rest, the same dedupe, the same "only drivers this tenant
 * holds" rule — and a selection that quietly disagreed between the two surfaces is the kind of bug
 * nobody reports, because each screen looks right on its own.
 */

/** What the roster endpoint will serve in one page (`Math.min(200, …)` server-side). */
const PAGE = 200

/**
 * Pages in flight at once.
 *
 * Not `Promise.all` over every remaining page: a ten-thousand-driver tenant is fifty reads, and firing
 * them from one button press is a burst the roster endpoint answers with timeouts — which reach the
 * operator as "could not select every driver". Not one at a time either, which is what it replaced:
 * four at a time keeps a fleet-sized select under a second's wait without the spike.
 */
const CONCURRENCY = 4

/** Not exported: both callers destructure the result, and a name nobody imports is a dead
 *  export the repo's guard refuses. */
interface RosterSelection {
    /** Matching driver account ids, deduplicated. */
    ids: string[]
    /** Claimed drivers the search returned. */
    claimed: number
    /** Claimed drivers the predicate refused — what the caller reports as skipped. */
    skipped: number
}

/**
 * Walk the roster for `search` and keep the drivers `match` accepts.
 *
 * `cancelled` is asked between waves, so a search the operator has already changed stops paying for
 * pages nobody will use — and, more importantly, cannot land its answer over a newer selection.
 */
export async function collectRosterDriverIds(
    search: string,
    match: (driver: BoxTruckVerification) => boolean,
    opts: { cancelled?: () => boolean } = {}
): Promise<RosterSelection> {
    const found: string[] = []
    let claimed = 0
    let skipped = 0
    const take = (items: BoxTruckVerification[]) => {
        // Only drivers this tenant actually holds. A row without a claim is somebody else's, or nobody's.
        const mine = items.filter((d) => !!d.claimDriverId)
        claimed += mine.length
        for (const driver of mine) {
            if (match(driver)) found.push(driver.userId)
            else skipped += 1
        }
    }

    const first = await fetchMyBoxTruckDrivers(1, PAGE, search)
    if (opts.cancelled?.()) return { ids: [], claimed: 0, skipped: 0 }
    take(first.items)

    // `first.items.length` as well as the count: a page that comes back short or empty ends the walk,
    // so a total that disagrees with the rows cannot spin this forever.
    const pages = first.items.length ? Math.ceil(first.total / PAGE) : 1
    for (let next = 2; next <= pages; next += CONCURRENCY) {
        const wave = Array.from({ length: Math.min(CONCURRENCY, pages - next + 1) }, (_, i) => fetchMyBoxTruckDrivers(next + i, PAGE, search))
        const landed = await Promise.all(wave)
        if (opts.cancelled?.()) return { ids: [], claimed: 0, skipped: 0 }
        for (const page of landed) take(page.items)
    }

    // The createdAt-sorted pages have no `_id` tie-breaker, so a claim created or released mid-walk can
    // straddle a page boundary and surface twice. A recipient list must not double-count it.
    return { ids: Array.from(new Set(found)), claimed, skipped }
}
